import { Directive, ElementRef, HostListener, Input, OnDestroy, Optional } from '@angular/core';

@Directive({
    selector: '[appTooltip]',
})
export class TooltipDirective implements OnDestroy {
    @Input() tooltip = ''; // The text for the tooltip to display
    @Input() top?= 0; // alignment from top
    @Input() left?= 0; // alignment from left
    @Input() fontSize?= 14; // For custom font size
    @Input() widthClass?= ''; // additional class
    @Input() heightClass?= ''; // additional class
    @Input() videoUrl?: string; // video url
    @Optional() @Input() tooltipClass? = 'tooltip-container';
    
    // Add delay configuration options
    @Input() showDelay? = 0; // delay before showing tooltip in ms
    @Input() hideDelay? = 0; // delay before hiding tooltip in ms

    private myPopup;
    isBoxHover = false;
    private readonly MARGIN = 4; // Margin from window edges
    private showTimeoutId: NodeJS.Timeout = null;
    private hideTimeoutId: NodeJS.Timeout = null;

    constructor(private el: ElementRef) { }

    // Clean up tooltip when directive is destroyed
    ngOnDestroy(): void {
        this.clearTimeouts();
        if (this.myPopup) {
            this.myPopup.remove();
        }
    }

    // Clear any pending timeouts
    private clearTimeouts(): void {
        if (this.showTimeoutId) {
            clearTimeout(this.showTimeoutId);
            this.showTimeoutId = null;
        }
        if (this.hideTimeoutId) {
            clearTimeout(this.hideTimeoutId);
            this.hideTimeoutId = null;
        }
    }

    // Show tooltip on mouse enter after delay
    @HostListener('mouseenter') onMouseEnter() {
        this.clearTimeouts();
        
        // Remove any existing tooltips first
        this.removeExistingTooltips();
        
        this.showTimeoutId = setTimeout(() => {
            this.createTooltipPopup();
        }, this.showDelay);
    }

    // Hide tooltip on mouse leave after short delay
    @HostListener('mouseleave') onMouseLeave() {
        this.clearTimeouts();
        
        if (!this.isBoxHover) {
            this.hideTimeoutId = setTimeout(() => {
                if (this.myPopup) {
                    this.fadeOutTooltip();
                }
            }, this.hideDelay);
        }
    }

    // Remove any existing tooltips
    private removeExistingTooltips() {
        const existingTooltips = document.getElementsByClassName(this.tooltipClass);
        if (existingTooltips && existingTooltips.length > 0) {
            // Convert HTMLCollection to Array to safely iterate and remove
            Array.from(existingTooltips).forEach(tooltip => {
                tooltip.remove();
            });
        }
    }

    // Add fade out animation and remove tooltip
    private fadeOutTooltip() {
        if (!this.myPopup) return;
        
        let a = this.myPopup;
        a.classList.add('fade-out');
        a.addEventListener('transitionend', () => {
            if (this.myPopup) {
                this.myPopup.remove();
                this.myPopup = null;
            }
        });
    }

    // Calculate optimal position for tooltip
    private calculatePosition(tooltipWidth: number, tooltipHeight: number): {x: number, y: number} {
        const triggerRect = this.el.nativeElement.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        
        // Calculate available space in each direction
        const spaceAbove = triggerRect.top;
        const spaceBelow = windowHeight - triggerRect.bottom;
        const spaceLeft = triggerRect.left;
        const spaceRight = windowWidth - triggerRect.right;

        // Default position (prefer top and right if space available)
        let x, y;

        // Horizontal positioning (prefer right side)
        // Try to center horizontally first
        const centerX = triggerRect.left + (triggerRect.width - tooltipWidth) / 2;
        if (centerX >= this.MARGIN && centerX + tooltipWidth <= windowWidth - this.MARGIN) {
            x = centerX;
        }
        // If not enough space to center, try right side
        else if (spaceRight >= tooltipWidth + this.MARGIN) {
            x = triggerRect.right + this.MARGIN;
        }
        // Otherwise try left side
        else if (spaceLeft >= tooltipWidth + this.MARGIN) {
            x = triggerRect.left - tooltipWidth - this.MARGIN;
        }
        // If no good position found, constrain to viewport bounds
        else {
            x = Math.max(this.MARGIN, Math.min(windowWidth - tooltipWidth - this.MARGIN,
                triggerRect.left + (triggerRect.width - tooltipWidth) / 2));
        }

        // Vertical positioning (prefer above)
        if (spaceAbove >= tooltipHeight + this.MARGIN) {
            y = triggerRect.top - tooltipHeight - this.MARGIN;
        } else if (spaceBelow >= tooltipHeight + this.MARGIN) {
            y = triggerRect.bottom + this.MARGIN;
        } else {
            // Center vertically if no space above or below
            y = Math.max(this.MARGIN, Math.min(windowHeight - tooltipHeight - this.MARGIN,
                triggerRect.top + (triggerRect.height - tooltipHeight) / 2));
        }

        return {x, y};
    }

    // Create and position the tooltip popup
    private createTooltipPopup() {
        // Remove any existing tooltips first
        this.removeExistingTooltips();
        
        if (this.tooltip.length) {
            // Create tooltip element
            let popup = document.createElement('div');
            popup.innerHTML = this.tooltip;
            popup.setAttribute('class', `${this.tooltipClass} high-elevated ${this.widthClass}`);

            // Set initial styles
            if (this.fontSize) {
                popup.style.fontSize = this.fontSize.toString() + 'px';
            }
            if (this.heightClass) {
                popup.style.maxHeight = this.heightClass + 'px';
                popup.style.overflowY = 'auto';
            }

            // Add to document temporarily to get dimensions
            popup.style.visibility = 'hidden';
            document.body.appendChild(popup);
            const tooltipWidth = popup.offsetWidth;
            const tooltipHeight = popup.offsetHeight;

            // Calculate optimal position
            const {x, y} = this.calculatePosition(tooltipWidth, tooltipHeight);
            
            // Set final position
            popup.style.top = y.toString() + 'px';
            popup.style.left = x.toString() + 'px';
            popup.style.visibility = 'visible';

            // Add video link if URL provided
            if (this.videoUrl && this.videoUrl.length) {
                let videoLink = document.createElement('a');
                videoLink.href = this.videoUrl;
                videoLink.target = '_blank';
                videoLink.textContent = 'Watch Video';
                videoLink.classList.add('tooltip-link');
                popup.appendChild(videoLink);
            }

            // Add hover handlers for tooltip itself
            popup.addEventListener('mouseenter', () => {
                this.isBoxHover = true;
                this.clearTimeouts();
            });
            popup.addEventListener('mouseleave', () => {
                this.isBoxHover = false;
                this.fadeOutTooltip();
            });

            this.myPopup = popup;
        }
    }
}
